/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\ClientsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the openvpn module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { Download, Ban, Trash2, Users, Wifi, WifiOff, Plus } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import type { OpenVPNClient, ClientFormat } from '../types';

interface ClientsTabProps {
    clients: OpenVPNClient[];
    loading: boolean;
    onDelete: (clientId: number) => Promise<boolean>;
    onRevoke: (clientId: number) => Promise<boolean>;
    onDownload: (clientId: number) => Promise<boolean>;
}

export function ClientsTab({
    clients,
    loading,
    onDelete,
    onRevoke,
    onDownload
}: ClientsTabProps) {
    const { t } = useI18n();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState<OpenVPNClient | null>(null);

    const handleDelete = async () => {
        if (selectedClient) {
            const success = await onDelete(selectedClient.id);
            if (success) {
                setShowDeleteModal(false);
                setSelectedClient(null);
            }
        }
    };

    const handleRevoke = async () => {
        if (selectedClient) {
            const success = await onRevoke(selectedClient.id);
            if (success) {
                setShowRevokeModal(false);
                setSelectedClient(null);
            }
        }
    };

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFormatLabel = (format: ClientFormat) => {
        return t(`openvpn.clients.formats.${format}`, format);
    };

    const getStatusBadge = (client: OpenVPNClient) => {
        if (client.revokedAt) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                    <Ban className="h-3 w-3" />
                    {t('openvpn.clients.status.revoked', 'Revoked')}
                </span>
            );
        }
        if (client.isConnected) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    <Wifi className="h-3 w-3" />
                    {t('openvpn.clients.status.connected', 'Connected')}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                <WifiOff className="h-3 w-3" />
                {t('openvpn.clients.status.disconnected', 'Disconnected')}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Clients List */}
            {clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 bg-card border border-border rounded-lg">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <h4 className="text-lg font-medium text-foreground mb-1">
                        {t('openvpn.clients.noClients', 'No clients configured')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                        {t('openvpn.clients.noClientsDesc', "Click 'Add' to create a new VPN client certificate")}
                    </p>
                </div>
            ) : (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border bg-muted/50">
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.name', 'Name')}
                                </th>
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.format', 'Format')}
                                </th>
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.status', 'Status')}
                                </th>
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.virtualAddress', 'Virtual IP')}
                                </th>
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.realAddress', 'Real IP')}
                                </th>
                                <th className="text-left p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.traffic', 'Traffic')}
                                </th>
                                <th className="text-right p-3 text-sm font-medium text-muted-foreground">
                                    {t('openvpn.clients.columns.actions', 'Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((client) => (
                                <tr key={client.id} className="border-b border-border last:border-b-0 hover:bg-muted/30">
                                    <td className="p-3">
                                        <div>
                                            <div className="font-medium text-foreground">{client.name}</div>
                                            {client.fixedIp && (
                                                <div className="text-xs text-muted-foreground">IP: {client.fixedIp}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                            {getFormatLabel(client.format)}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        {getStatusBadge(client)}
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">
                                        {client.virtualAddress || '-'}
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">
                                        {client.realAddress || '-'}
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">
                                        {client.isConnected ? (
                                            <span>
                                                ? {formatBytes(client.bytesReceived)} / ? {formatBytes(client.bytesSent)}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center justify-end gap-1">
                                            {!client.revokedAt && (
                                                <>
                                                    <button
                                                        onClick={() => onDownload(client.id)}
                                                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                                        title={t('openvpn.clients.actions.download', 'Download Config')}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedClient(client);
                                                            setShowRevokeModal(true);
                                                        }}
                                                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 transition-colors"
                                                        title={t('openvpn.clients.actions.revoke', 'Revoke Certificate')}
                                                    >
                                                        <Ban className="h-4 w-4" />
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedClient(client);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 transition-colors"
                                                title={t('openvpn.clients.actions.delete', 'Delete')}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Revoke Confirmation Modal */}
            <ConfirmationModal
                isOpen={showRevokeModal}
                onClose={() => {
                    setShowRevokeModal(false);
                    setSelectedClient(null);
                }}
                onConfirm={handleRevoke}
                title={t('openvpn.confirmRevoke.title', 'Revoke Certificate')}
                message={t('openvpn.confirmRevoke.message', { name: selectedClient?.name || '' })}
                confirmText={t('openvpn.clients.actions.revoke', 'Revoke Certificate')}
                type="danger"
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedClient(null);
                }}
                onConfirm={handleDelete}
                title={t('openvpn.confirmDelete.title', 'Delete Client')}
                message={t('openvpn.confirmDelete.message', { name: selectedClient?.name || '' })}
                confirmText={t('common.delete', 'Delete')}
                type="danger"
            />
        </div>
    );
}
